import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar, Footer } from "@/components/Layout";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { ShareButtons } from "@/components/ShareButtons";
import { api, API } from "@/lib/api";
import {
  WrenchIcon, LightningIcon, HammerIcon, PaintRollerIcon, WallIcon,
  WindIcon, HouseLineIcon, StackIcon, BuildingsIcon, ArrowRightIcon,
  CheckCircleIcon, ClockIcon, ShieldCheckIcon, PhoneCallIcon, StarIcon,
} from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjb250cmFjdG9yJTIwd29ya2luZ3xlbnwwfHx8fDE3ODU1ODM3ODB8MA&ixlib=rb-4.1.0&q=85";
const SERVICES_IMG = "https://images.unsplash.com/photo-1645651964715-d200ce0939cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxjYXJwZW50cnklMjB0b29sc3xlbnwwfHx8fDE3ODU1ODM3ODB8MA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: WrenchIcon, name: "Plumbing", desc: "Leaks, fixtures, water heaters & drains." },
  { icon: LightningIcon, name: "Electrical", desc: "Outlets, panels, lighting & wiring." },
  { icon: HammerIcon, name: "Carpentry", desc: "Framing, trim, doors & custom builds." },
  { icon: PaintRollerIcon, name: "Painting", desc: "Interior & exterior finishing." },
  { icon: WallIcon, name: "Drywall", desc: "Patching, hanging & texture repair." },
  { icon: WindIcon, name: "HVAC", desc: "Heating, cooling & ventilation." },
  { icon: HouseLineIcon, name: "Roofing", desc: "Leak repair, shingles & flashing." },
  { icon: StackIcon, name: "Flooring", desc: "Tile, hardwood, vinyl & subfloor." },
  { icon: BuildingsIcon, name: "Masonry", desc: "Brick, block, stone & concrete." },
];

const steps = [
  { n: "01", t: "Describe the job", d: "Tell us what needs fixing in a quick guided form." },
  { n: "02", t: "We reach out", d: "Our team reviews and contacts you to schedule." },
  { n: "03", t: "Get it fixed", d: "A skilled pro shows up and handles the repair." },
];

export default function Landing() {
  const [reviews, setReviews] = useState([]);
  const [showcase, setShowcase] = useState([]);
  useEffect(() => {
    api.get("/reviews/public").then((r) => {
      const top = [...(r.data.reviews || [])].sort((a, b) => b.rating - a.rating).slice(0, 3);
      setReviews(top);
    }).catch(() => {});
    api.get("/showcase/public").then((r) => {
      setShowcase((r.data.items || []).slice(0, 6));
    }).catch(() => {});
  }, []);
  return (
    <div className="bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-xs tracking-[0.2em] uppercase font-bold text-gold-600 mb-5"
            >
              Commercial & Residential Repair
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.05]"
            >
              Every repair.<br />One trusted crew.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 text-base md:text-lg text-slate-600 max-w-lg leading-relaxed"
            >
              From a leaky faucet to a full commercial build-out, MCC Service Corp handles it all.
              Tell us what you need and we'll be on the job fast.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/request"
                data-testid="hero-cta-request"
                className="group inline-flex items-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-7 py-3.5 transition-colors active:scale-95"
              >
                Request Service
                <ArrowRightIcon size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/track"
                data-testid="hero-cta-track"
                className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-gold-600 text-slate-900 font-semibold px-7 py-3.5 transition-colors bg-white"
              >
                Track a Request
              </Link>
            </motion.div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
              {[[ShieldCheckIcon, "Licensed & insured"], [ClockIcon, "Fast response"], [CheckCircleIcon, "Upfront quotes"]].map(([Ic, t]) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <Ic size={18} weight="fill" className="text-gold-600" /> {t}
                </span>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-gold-600 rounded-sm" />
              <img
                src={HERO_IMG}
                alt="Contractor at work"
                className="relative rounded-sm w-full h-[420px] object-cover border border-slate-300"
              />
              <div className="absolute -bottom-5 -right-5 bg-navy-900 text-white rounded-sm px-6 py-4 shadow-crisp">
                <p className="font-heading text-2xl font-extrabold">9+</p>
                <p className="text-xs text-slate-300 uppercase tracking-wider">Trades covered</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-600 mb-3">What we fix</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Full-service repairs, done right
            </h2>
          </div>
          <p className="text-slate-600 max-w-sm text-sm md:text-base">
            Select any combination of services in your request — we send the right specialist.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              data-testid={`service-card-${s.name.toLowerCase()}`}
              className="group bg-white border border-slate-200 rounded-sm p-6 transition-transform hover:-translate-y-1 hover:border-gold-600 hover:shadow-crisp"
            >
              <span className="inline-flex w-12 h-12 rounded-sm bg-navy-900 group-hover:bg-gold-600 items-center justify-center transition-colors mb-5">
                <s.icon size={24} weight="duotone" className="text-gold-500 group-hover:text-white transition-colors" />
              </span>
              <h3 className="font-heading text-xl font-semibold text-slate-900">{s.name}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-500 mb-3">How it works</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10">
              Three steps to a finished job
            </h2>
            <div className="space-y-8">
              {steps.map((st) => (
                <div key={st.n} className="flex gap-5">
                  <span className="font-mono text-gold-500 text-lg font-semibold">{st.n}</span>
                  <div>
                    <h3 className="font-heading text-xl font-semibold">{st.t}</h3>
                    <p className="text-slate-400 text-sm mt-1">{st.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/request"
              data-testid="how-cta-request"
              className="mt-10 inline-flex items-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-7 py-3.5 transition-colors active:scale-95"
            >
              Start your request <ArrowRightIcon size={18} weight="bold" />
            </Link>
          </div>
          <div className="relative">
            <img src={SERVICES_IMG} alt="Tools" className="rounded-sm w-full h-[380px] object-cover border border-slate-700" />
          </div>
        </div>
      </section>

      {showcase.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-showcase">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-gold-600 mb-3">Before &amp; After</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-3">See the transformation</h2>
          <p className="text-slate-500 mb-10 max-w-2xl">Real jobs, real results — drag the slider to see exactly what our team delivered.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {showcase.map((s, i) => (
              <div key={i} data-testid={`home-showcase-${i}`}>
                <BeforeAfterSlider before={`${API}/files/${s.before}`} after={`${API}/files/${s.after}`} />
                <div className="mt-2 flex items-center justify-between gap-2">
                  {s.caption ? (
                    <p className="text-sm text-slate-700 font-medium">{s.caption}</p>
                  ) : (s.service || s.property_type) ? (
                    <p className="text-sm text-slate-600 capitalize">{[s.service, s.property_type].filter(Boolean).join(" · ")}</p>
                  ) : <span />}
                  <ShareButtons
                    tone="light"
                    url={typeof window !== "undefined" ? window.location.origin : ""}
                    text="See the before & after transformations by MCC Service Corp."
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {reviews.length > 0 && (
        <section className="bg-navy-900 text-white" data-testid="home-reviews">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-gold-500 mb-3">What customers say</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-10">Trusted by homeowners &amp; businesses</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <div key={i} data-testid={`home-review-${i}`} className="bg-navy-800 border border-navy-700 rounded-sm p-6">
                  <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => <StarIcon key={n} size={18} weight={n <= r.rating ? "fill" : "regular"} className="text-gold-500" />)}
                  </div>
                  {r.comment && <p className="text-slate-200 leading-relaxed">"{r.comment}"</p>}
                  {r.reply && (
                    <p className="mt-3 text-sm text-slate-400 border-l-2 border-gold-500 pl-3">
                      <span className="font-semibold text-gold-500">MCC Service Corp.:</span> {r.reply}
                    </p>
                  )}
                  <p className="mt-4 font-heading font-semibold">{r.name}</p>
                  <p className="text-xs text-slate-400 capitalize">{[r.service, r.property_type].filter(Boolean).join(" · ")}</p>
                </div>
              ))}
            </div>
            <Link to="/ratings" data-testid="home-reviews-cta" className="mt-8 inline-flex items-center gap-2 text-gold-500 font-semibold hover:text-gold-400 transition-colors">
              See all ratings <ArrowRightIcon size={16} weight="bold" />
            </Link>
          </div>
        </section>
      )}

      {/* CTA banner */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="bg-gold-600 rounded-sm px-8 md:px-14 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <PhoneCallIcon size={40} weight="fill" className="text-white shrink-0" />
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Got a repair that can't wait?</h2>
              <p className="text-gold-100 text-sm mt-1">Submit a request and we'll get right back to you.</p>
            </div>
          </div>
          <Link
            to="/request"
            data-testid="banner-cta-request"
            className="rounded-sm bg-navy-900 hover:bg-navy-800 text-white font-semibold px-8 py-3.5 transition-colors active:scale-95 whitespace-nowrap"
          >
            Get a Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
